import { faTrash } from "@fortawesome/duotone-light-svg-icons"
import { faImage } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  JoonUIColor,
  SPACING,
  Switch,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useRef } from "react"
import { Trans, useTranslation } from "react-i18next"

import { useCompletionRequirement } from "./useCompletionRequirement"
import AnimatedStars from "../../../../components/animatedStars/AnimatedStars"
import Counter from "../../../../components/counter/Counter"
import { useImageModalStore } from "../../../../components/modals/ImageModal"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

export const CompletionRequirementContent = () => {
  const {
    reqReview,
    reqPhoto,
    reqNotes,
    setReqPhoto,
    maxAutoVerifyCount,
    setMaxAutoVerifyCount,
    completionCriteriaNotes,
    setCompletionCriteriaNotes,
    completionCriteriaPhotoFile,
    setCompletionCriteriaPhotoFile,
    completionCriteriaPhotoUrl,
    setCompletionCriteriaPhotoUrl,
  } = useAddEditQuestStore()
  const { toggleReview, togglePhoto, toggleNotes } = useCompletionRequirement()
  const { onOpen: onOpenImageModal } = useImageModalStore()
  const { t } = useTranslation()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const isAutoVerifyEnabled = maxAutoVerifyCount !== null

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCompletionCriteriaPhotoFile(file)
      setCompletionCriteriaPhotoUrl(URL.createObjectURL(file))
    }
  }

  const handleRemovePhoto = () => {
    setCompletionCriteriaPhotoFile(null)
    setCompletionCriteriaPhotoUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  useEffect(() => {
    if (completionCriteriaPhotoFile) {
      setCompletionCriteriaPhotoUrl(
        URL.createObjectURL(completionCriteriaPhotoFile)
      )
    }
  }, [completionCriteriaPhotoFile, setCompletionCriteriaPhotoUrl])

  return (
    <div className="flex flex-col gap-2 w-full">
      <button
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: SPACING.space2,
          padding: SPACING.space3,
          borderRadius: SPACING.space2,
          border: `1px solid ${JoonUIColor.border.default}`,
          background: JoonUIColor.background.primaryNeutral,
          cursor: "default",
        }}
      >
        <div className="flex flex-row gap-2 items-center justify-between">
          <Typography variant="bodySmall">
            {t("QUEST_REQUIRE_REVIEW")}
          </Typography>
          <Switch
            checked={reqReview}
            onChange={toggleReview}
            name="requires-review"
          />
        </div>
        <Typography
          variant="bodyXSmall"
          color={JoonUIColor.text.secondary}
          textAlign="left"
        >
          {t("QUEST_REQUIRE_REVIEW_HELP")}
        </Typography>
      </button>

      {reqReview && (
        <div className="flex flex-col gap-2 w-full">
          <div className="flex-col gap-2 bg-gradient-to-r from-white from-30% to-pink-100 border rounded-md border-borderPrimary p-3">
            <div className="flex flex-row gap-2 items-center justify-between">
              <div className="flex flex-row gap-2 items-center">
                <AnimatedStars animate={false} scale={0.7} />
                <Typography variant="bodySmall">
                  {t("QUEST_ENABLE_AI_REVIEW")}
                </Typography>
              </div>
              <Switch
                checked={maxAutoVerifyCount !== null}
                onChange={() => {
                  if (maxAutoVerifyCount === null) {
                    setMaxAutoVerifyCount(0)
                    setReqPhoto(true)
                  } else {
                    setMaxAutoVerifyCount(null)
                  }
                }}
                name="set-auto-verify-count"
              />
            </div>
            {maxAutoVerifyCount !== null && (
              <>
                <div className="flex flex-col gap-2 bg-white p-3 border border-borderPrimary rounded-md mt-2">
                  <Typography variant="bodySmall" textAlign="left">
                    {t("QUEST_RETRIES_QUESTION")}
                  </Typography>
                  <Counter
                    value={maxAutoVerifyCount || 0}
                    setValue={setMaxAutoVerifyCount}
                    maxValue={5}
                    variant="subtle"
                  />
                  {maxAutoVerifyCount === 0 ? (
                    <Typography variant="bodyXSmall" textAlign="left">
                      <Trans
                        i18nKey="QUEST_NO_RETRIES_FULL"
                        components={{
                          b: (
                            <Typography variant="bodyXSmallBold">
                              {""}
                            </Typography>
                          ),
                        }}
                      />
                    </Typography>
                  ) : (
                    <Typography variant="bodyXSmall" textAlign="left">
                      {t("QUEST_RETRIES_HELP", { count: maxAutoVerifyCount })}
                    </Typography>
                  )}
                </div>

                <div className="flex flex-col gap-2 bg-white p-3 border border-borderPrimary rounded-md mt-2">
                  <Typography variant="bodySmall" textAlign="left">
                    {t("QUEST_COMPLETION_CRITERIA_PROMPT")}
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: "none" }}
                    id="completion-criteria-photo-upload"
                  />

                  {completionCriteriaPhotoUrl ? (
                    <div className="relative w-full flex flex-row items-center border border-borderPrimary rounded-md">
                      <button
                        className="w-full h-full"
                        onClick={() =>
                          onOpenImageModal(completionCriteriaPhotoUrl)
                        }
                      >
                        <img
                          src={completionCriteriaPhotoUrl}
                          alt="Completion criteria sample"
                          className="w-full rounded-md border border-borderPrimary flex-1 max-h-[60px] object-cover cursor-pointer"
                        />
                      </button>

                      <button
                        onClick={handleRemovePhoto}
                        className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-100 p-2"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          color={JoonUIColor.icon.neutral}
                          fontSize={16}
                        />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="completion-criteria-photo-upload"
                      className="flex items-center gap-2 p-3 border border-borderPrimary rounded-md cursor-pointer hover:bg-gray-50"
                    >
                      <FontAwesomeIcon
                        icon={faImage}
                        size="sm"
                        color={JoonUIColor.icon.neutral}
                      />
                      <Typography
                        variant="bodySmall"
                        color={JoonUIColor.text.placeholder}
                      >
                        {t("QUEST_UPLOAD_SAMPLE")}
                      </Typography>
                    </label>
                  )}

                  <TextInput
                    name="completion-criteria-notes"
                    value={completionCriteriaNotes || ""}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value.length <= 255) {
                        setCompletionCriteriaNotes(value)
                      }
                    }}
                    placeholder={t("QUEST_CRITERIA_NOTES_PH")}
                    fullWidth
                    maxLength={255}
                  />

                  <Typography
                    variant="bodyXSmall"
                    color={JoonUIColor.text.secondary}
                    textAlign="left"
                  >
                    {t("QUEST_AI_USE_HELP")}
                  </Typography>
                </div>
              </>
            )}
          </div>
          <Typography variant="bodySmall" className="mt-2" textAlign="left">
            {isAutoVerifyEnabled
              ? t("QUEST_AI_WILL_REVIEW")
              : t("QUEST_YOU_WILL_REVIEW")}
          </Typography>
          <div className="w-full border border-borderPrimary rounded-md overflow-hidden p-3 bg-white">
            <div className="flex flex-row gap-2 items-center justify-between">
              <div className="flex flex-col">
                <Typography variant="bodySmall">
                  {t("QUEST_COMPLETION_PHOTO")}
                </Typography>
                {isAutoVerifyEnabled && (
                  <Typography
                    variant="bodyXSmall"
                    color={JoonUIColor.text.secondary}
                    textAlign="left"
                  >
                    {t("QUEST_REQUIRED_FOR_AI")}
                  </Typography>
                )}
              </div>
              <Switch
                checked={reqPhoto}
                onChange={togglePhoto}
                name="requires-photo"
                disabled={isAutoVerifyEnabled}
              />
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center justify-between bg-white p-3 rounded-md border border-borderPrimary">
            <Typography variant="bodySmall">
              {t("QUEST_COMPLETION_NOTES")}
            </Typography>
            <Switch
              checked={reqNotes}
              onChange={toggleNotes}
              name="requires-notes"
            />
          </div>
          <Typography
            variant="bodyXSmall"
            textAlign="left"
            color={JoonUIColor.text.secondary}
          >
            {t("QUEST_PHOTO_NOTES_HELP")}
          </Typography>
        </div>
      )}
    </div>
  )
}
